import React, { useState, useEffect } from "react";
import { supabase } from "../../lib/supabaseClient";

export default function QuickCreateAuctionForm() {
  const [nom, setNom] = useState("");
  const [ville, setVille] = useState("");
  const [pays, setPays] = useState("");
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [houseId, setHouseId] = useState("");
  const [specialite, setSpecialite] = useState("");
  const [specialiteOther, setSpecialiteOther] = useState("");
  const [artists, setArtists] = useState("");
  const [msg, setMsg] = useState("");
  
  // État pour les maisons de ventes
  const [houses, setHouses] = useState([]);
  const [housesLoading, setHousesLoading] = useState(false);
  const [showNewHouseModal, setShowNewHouseModal] = useState(false);
  const [newHouse, setNewHouse] = useState({
    name: "",
    city: "",
    country: ""
  });

  // Charger les maisons de ventes au montage
  useEffect(() => {
    loadHouses();
  }, []);

  async function loadHouses() {
    try {
      setHousesLoading(true);
      const { data, error } = await supabase
        .from("auction_houses")
        .select("id, name, city, country, is_top_ten")
        .eq("active", true)
        .order("is_top_ten", { ascending: false })
        .order("name", { ascending: true });
      
      if (error) throw error;
      setHouses(data || []);
    } catch (e) {
      console.error("Erreur chargement maisons:", e);
    } finally {
      setHousesLoading(false);
    }
  }

  async function createNewHouse() {
    if (!newHouse.name.trim()) {
      alert("Le nom de la maison est requis");
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from("auction_houses")
        .insert([{
          name: newHouse.name.trim(),
          city: newHouse.city?.trim() || null,
          country: newHouse.country?.trim() || null,
          active: true
        }])
        .select()
        .single();
      
      if (error) throw error;
      
      // Recharger la liste et sélectionner la nouvelle maison
      await loadHouses();
      setHouseId(data.id);
      setShowNewHouseModal(false);
      setNewHouse({ name: "", city: "", country: "" });
    } catch (e) {
      console.error("Erreur création maison:", e);
      alert("Impossible de créer la maison : " + e.message);
    }
  }

  async function save(e) {
    e.preventDefault();
    
    // Validation
    if (!nom.trim()) {
      setMsg("❌ Le nom de la vente est requis");
      return;
    }
    
    setMsg("Enregistrement…");
    
    try {
      const arr = artists.split(/[,;|\n]+/).map(s => s.trim()).filter(Boolean);
      
      const { error } = await supabase.rpc("fn_upsert_auction_event", {
        p_id: null,
        p_nom: nom || null,
        p_ville: ville || null,
        p_pays: pays || null,
        p_date_debut: dateDebut || null,
        p_date_fin: dateFin || null,
        p_auction_house_id: houseId || null,
        p_specialite: (specialite === "autre" ? (specialiteOther || null) : (specialite || null)),
        p_artists: arr.length ? arr : null
      });
      
      if (error) throw error;
      
      setMsg("✅ Vente enregistrée");
      
      // Reset du formulaire
      setNom("");
      setVille("");
      setPays("");
      setDateDebut("");
      setDateFin("");
      setHouseId("");
      setSpecialite("");
      setSpecialiteOther("");
      setArtists("");
    } catch (e) {
      console.error("Erreur enregistrement:", e);
      setMsg("❌ " + e.message);
    }
  }

  return (
    <>
      <form onSubmit={save} className="space-y-4">
        {/* Nom de la vente */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nom de la vente *
          </label>
          <input
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
            placeholder="Ex: 21st Century Evening Sale"
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            required
          />
        </div>

        {/* Maison de ventes - SELECT STANDARD */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Maison de ventes
          </label>
          <div className="flex gap-2">
            <select
              value={houseId}
              onChange={(e) => setHouseId(e.target.value)}
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black"
              disabled={housesLoading}
            >
              <option value="">— Sélectionner une maison —</option>
              {houses.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.is_top_ten ? "⭐ " : ""}
                  {h.name}
                  {h.city ? ` — ${h.city}` : h.country ? ` — ${h.country}` : ""}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setShowNewHouseModal(true)}
              className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm whitespace-nowrap font-medium"
              title="Créer une nouvelle maison"
            >
              + Nouvelle
            </button>
          </div>
        </div>

        {/* Localisation */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="Paris, New York…"
              value={ville}
              onChange={(e) => setVille(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pays</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="France, États-Unis…"
              value={pays}
              onChange={(e) => setPays(e.target.value)}
            />
          </div>
        </div>

        {/* Spécialité */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Spécialité
          </label>
          <select
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            value={specialite}
            onChange={(e) => setSpecialite(e.target.value)}
          >
            <option value="">— Sélectionner —</option>
            <option value="Impressionnistes & modernes">Impressionnistes & modernes</option>
            <option value="Après-guerre & contemporain">Après-guerre & contemporain</option>
            <option value="Art contemporain (20–21e)">Art contemporain (20–21e)</option>
            <option value="Art moderne (1900–45)">Art moderne (1900–45)</option>
            <option value="Old Masters">Old Masters</option>
            <option value="Design">Design</option>
            <option value="Photographie">Photographie</option>
            <option value="Art urbain">Art urbain</option>
            <option value="Arts d'Asie">Arts d'Asie</option>
            <option value="Arts d'Afrique & Océanie">Arts d'Afrique & Océanie</option>
            <option value="Arts décoratifs / Art déco">Arts décoratifs / Art déco</option>
            <option value="autre">Autre (préciser)</option>
          </select>
          
          {specialite === "autre" && (
            <input
              className="mt-2 w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="Préciser la spécialité…"
              value={specialiteOther}
              onChange={(e) => setSpecialiteOther(e.target.value)}
            />
          )}
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date début</label>
            <input
              type="date"
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              value={dateDebut}
              onChange={(e) => setDateDebut(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date fin</label>
            <input
              type="date"
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              value={dateFin}
              onChange={(e) => setDateFin(e.target.value)}
            />
          </div>
        </div>

        {/* Artistes vedettes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Artistes vedettes
          </label>
          <input
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            placeholder="Picasso, Kusama, Basquiat (séparés par virgules)"
            value={artists}
            onChange={(e) => setArtists(e.target.value)}
          />
          <p className="text-xs text-gray-500 mt-1">
            Séparez les noms par des virgules, points-virgules ou retours à la ligne
          </p>
        </div>

        {/* Bouton submit + message */}
        <div className="flex items-center gap-3">
          <button
            type="submit"
            className="px-6 py-2.5 rounded-lg bg-black text-white font-medium hover:bg-gray-800 transition"
          >
            Enregistrer
          </button>
          {msg && (
            <div className={`text-sm ${msg.includes("✅") ? "text-green-700" : msg.includes("❌") ? "text-red-700" : "text-gray-600"}`}>
              {msg}
            </div>
          )}
        </div>
      </form>

      {/* Modale création nouvelle maison */}
      {showNewHouseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setShowNewHouseModal(false)}
          />
          <div className="relative z-10 w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Nouvelle maison de ventes</h3>
              <button
                onClick={() => setShowNewHouseModal(false)}
                className="rounded-md px-2 py-1 text-gray-500 hover:bg-gray-100"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom *
                </label>
                <input
                  type="text"
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                  value={newHouse.name}
                  onChange={(e) => setNewHouse(s => ({ ...s, name: e.target.value }))}
                  placeholder="Sotheby's, Christie's, Artcurial…"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ville
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-md border border-gray-300 px-3 py-2"
                    value={newHouse.city}
                    onChange={(e) => setNewHouse(s => ({ ...s, city: e.target.value }))}
                    placeholder="Paris, New York…"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pays
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-md border border-gray-300 px-3 py-2"
                    value={newHouse.country}
                    onChange={(e) => setNewHouse(s => ({ ...s, country: e.target.value }))}
                    placeholder="France, États-Unis…"
                  />
                </div>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                className="rounded-md border border-gray-300 px-3 py-2 hover:bg-gray-50"
                onClick={() => setShowNewHouseModal(false)}
              >
                Annuler
              </button>
              <button
                className="rounded-md bg-black px-3 py-2 text-white hover:bg-gray-800"
                onClick={createNewHouse}
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}